import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Brain, 
  DollarSign, 
  Code, 
  Users, 
  Zap, 
  Shield, 
  ArrowRight, 
  Star,
  CheckCircle,
  Globe,
  BarChart3,
  Sparkles,
  LucideLoaderPinwheel,
  Gift,
  Heart,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import './LandingPage.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  // Screenshot data
  const screenshots = [
    {
      src: "/landing_page_shots/one.png",
      title: "Discover Agents",
      description: "Find agents to chat with by searching on the Discover page."
    },
    {
      src: "/landing_page_shots/two.png", 
      title: "Agent Creation Interface",
      description: "Use the Build dashboard to upload files, add text, and scan websites to create your agent."
    },
    {
      src: "/landing_page_shots/three.png",
      title: "Marketplace Integration",
      description: "Review your agent's conversations with users to improve your agent and get paid."
    }
  ];
  
  console.log('LandingPage: Component rendering, isAuthenticated:', isAuthenticated);

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % screenshots.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isAutoPlaying, screenshots.length]);

  // Carousel navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % screenshots.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + screenshots.length) % screenshots.length);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  // Helper function to get header button text based on screen size
  const getHeaderButtonText = () => {
    const isMobile = window.innerWidth <= 768;
    
    if (isAuthenticated) {
      return 'Go to Build';
    } else if (isMobile) {
      return 'Request EA';
    } else {
      return 'Request Early Access';
    }
  };

  const handleRequestEarlyAccess = () => {
    // Early access deprecated - all users can access
    if (isAuthenticated) {
      navigate('/build');
    } else {
      // User needs to log in
      const isDevelopment = window.location.hostname === 'localhost';
      const oauthUrl = isDevelopment ? 'https://localhost:5001/auth/google' : '/auth/google';
      window.location.href = oauthUrl;
    }
  };

  const handleViewMarketplace = () => {
    navigate('/');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <div className="landing-page" data-theme="dark">
      {/* Navigation Header */}
      <header className="landing-header">
        <div className="container">
          <div className="header-content">
            <div className="logo" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
              <img 
                src={process.env.PUBLIC_URL + "/YourQuestionsAnsweredLogo.png"} 
                alt="Question Answer Pro Logo" 
                width={32} 
                height={32}
                style={{ objectFit: 'contain' }}
              />
              <span>Question Answer Pro</span>
            </div>
            <nav className="nav-links">
              <a href="#features">Features</a>
              <a href="#marketplace">Marketplace</a>
              <a href="#how-it-works">How It Works</a>

              <a href="/blog">Blog</a>
            </nav>
            <div className="header-cta">
              <button className="btn-secondary" onClick={handleViewMarketplace}>
                Browse Agents
              </button>
              <button 
                className="btn-primary" 
                style={{ fontSize: !isAuthenticated ? '0.7rem' : '1rem' }}
                onClick={handleRequestEarlyAccess}
              >
                {getHeaderButtonText()}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          {/* Placeholder for animated background - could be particles, gradients, or video */}
          <div className="hero-particles"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-badge">
            <div className="badge-brain">
              <img 
                src={process.env.PUBLIC_URL + "/YourQuestionsAnsweredLogo.png"} 
                alt="AI Brain" 
                width={80} 
                height={80}
                style={{ 
                  objectFit: 'contain',
                  imageRendering: 'smooth',
                  WebkitImageRendering: 'smooth'
                }}
              />
              <div className="badge-pulse pulse-1"></div>
              <div className="badge-pulse pulse-2"></div>
            </div>
          </div>
          
          <h1 className="hero-title">
            Build, Monetize & Deploy
            <span className="gradient-text"> AI Agents</span>
          </h1>
          
          <p className="hero-subtitle">
          Get answers at the speed of AI with the trust that comes from working with real experts, or make your own agent and share your expertise with the world!
          </p>
          
          <div className="hero-cta">
            <button className="cta-primary" onClick={handleRequestEarlyAccess}>
              {isAuthenticated 
                ? 'Go to Build'
                : 'Request Early Access'
              }
              <ArrowRight size={20} />
            </button>
            <button className="cta-secondary" onClick={handleViewMarketplace}>
              View Marketplace
            </button>
          </div>
          
          <div className="hero-stats">
            <div className="stat">
              <div className="stat-icon">
                <Users size={24} />
              </div>
              <div className="stat-content">
                <h4>Expert Knowledge</h4>
                <p>Answers at AI speed with human expert verification</p>
              </div>
            </div>
            <div className="stat">
              <div className="stat-icon">
                <Zap size={24} />
              </div>
              <div className="stat-content">
                <h4>Personal Use</h4>
                <p>Automate your own knowledge-based tasks</p>
              </div>
            </div>
            <div className="stat">
              <div className="stat-icon">
                <DollarSign size={24} />
              </div>
              <div className="stat-content">
                <h4>Monetize Skills</h4>
                <p>Turn your expertise into recurring revenue</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Homepage Screenshot */}
        <div className="hero-visual">
          <div className="screenshot-container">
            <img 
              src={process.env.PUBLIC_URL + "/homepage_screenshot.png"} 
              alt="Question Answer Pro Homepage Screenshot" 
              className="homepage-screenshot"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <div className="section-header">
            <h2>Everything You Need to Build AI Agents</h2>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <Brain size={32} />
              </div>
              <h3>Intelligent Creation</h3>
              <p>Build sophisticated AI agents in seconds. You provide your knowledge and let our intelligence layer do the rest.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <DollarSign size={32} />
              </div>
              <h3>Monetize Instantly</h3>
              <p>List your agent in our marketplace and start earning. Set your own prices and keep 85% of all revenue.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <Code size={32} />
              </div>
              <h3>Embed Anywhere</h3>
              <p>Deploy your agents on websites, apps, or platforms with simple embed codes. Works everywhere.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <LucideLoaderPinwheel size={32} />
              </div>
              <h3>Virtuous Cycles</h3>
              <p>The feedback you provide the agent and your responses to your users make your agent better through time.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Zap size={32} />
              </div>
              <h3>Lightning Fast</h3>
              <p>Optimized for speed and scale. Your agents respond in milliseconds, even under heavy load.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <Gift size={32} />
              </div>
              <h3>Start Free</h3>
              <p>Free forever plan available with all the features you need to derive value from your agent long term.</p>
            </div>
            
            
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works">
        <div className="container">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>Get started in minutes, not months</p>
          </div>
          
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Create Your Agent</h3>
                <p>Upload your knowledge in files, freeform text, or URLs.</p>
              </div>
              {/* Placeholder for step illustration */}
              <div className="step-visual">
                <div className="step-icon">📝</div>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Intelligence Layer</h3>
                <p>Our intelligence layer processes your data and derives the key facets of your expertise.</p>
              </div>
              {/* Placeholder for step illustration */}
              <div className="step-visual">
                <div className="step-icon">🧠</div>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Launch to the Marketplace</h3>
                <p>When you're ready, launch your agent to the marketplace and start earning!</p>
              </div>
              {/* Placeholder for step illustration */}
              <div className="step-visual">
                <div className="step-icon">💰</div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Marketplace Preview */}
      {/* <section id="marketplace" className="marketplace-preview">
        <div className="container">
          <div className="section-header">
            <h2>Featured Agents</h2>
            <p>Discover what others are building and earning</p>
          </div>
          
          <div className="agents-grid">
            <div className="agent-card">
              <div className="agent-avatar">
                {/* Placeholder for agent avatar */}
              {/*  <div className="avatar-placeholder">👨‍💼</div>
              </div>
              <div className="agent-info">
                <h3>Business Consultant Pro</h3>
                <p>Expert business advice and strategy consulting</p>
                <div className="agent-stats">
                  <span className="rating">⭐ 4.9</span>
                  <span className="price">$29/month</span>
                </div>
              </div>
            </div>
            
            <div className="agent-card">
              <div className="agent-avatar">
                <div className="avatar-placeholder">👩‍⚕️</div>
              </div>
              <div className="agent-info">
                <h3>Health & Wellness Coach</h3>
                <p>Personalized fitness and nutrition guidance</p>
                <div className="agent-stats">
                  <span className="rating">⭐ 4.8</span>
                  <span className="price">$19/month</span>
                </div>
              </div>
            </div>
            
            <div className="agent-card">
              <div className="agent-avatar">
                <div className="avatar-placeholder">👨‍💻</div>
              </div>
              <div className="agent-info">
                <h3>Code Mentor</h3>
                <p>Programming help and code review assistance</p>
                <div className="agent-stats">
                  <span className="rating">⭐ 4.9</span>
                  <span className="price">$39/month</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Pricing Section */}
      {/* <section id="pricing" className="pricing">
        <div className="container">
          <div className="section-header">
            <h2>Simple, Transparent Pricing</h2>
            <p>Start free, scale as you grow</p>
          </div>
          
          <div className="pricing-grid">
            <div className="pricing-card">
              <div className="pricing-header">
                <h3>Free</h3>
                <div className="price">
                  <span className="currency">$</span>
                  <span className="amount">0</span>
                  <span className="period">/month</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li>3 AI Agents</li>
                <li>50 conversations/month</li>
                <li>Basic file upload</li>
                <li>Community support</li>
              </ul>
              <button className="pricing-cta" onClick={handleRequestEarlyAccess}>
                Request Early Access
              </button>
            </div>
            
            <div className="pricing-card featured">
              <div className="pricing-badge">Most Popular</div>
              <div className="pricing-header">
                <h3>Pro</h3>
                <div className="price">
                  <span className="currency">$</span>
                  <span className="amount">19</span>
                  <span className="period">/month</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li>Unlimited Agents</li>
                <li>500 conversations/month</li>
                <li>1GB file storage</li>
                <li>Advanced analytics</li>
                <li>Priority support</li>
              </ul>
              <button className="pricing-cta" onClick={handleRequestEarlyAccess}>
                Request Early Access
              </button>
            </div>
            
            <div className="pricing-card">
              <div className="pricing-header">
                <h3>Enterprise</h3>
                <div className="price">
                  <span className="currency">$</span>
                  <span className="amount">99</span>
                  <span className="period">/month</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li>Everything in Pro</li>
                <li>Unlimited conversations</li>
                <li>10GB file storage</li>
                <li>API access</li>
                <li>White-label options</li>
                <li>Dedicated support</li>
              </ul>
              <button className="pricing-cta" onClick={handleRequestEarlyAccess}>
                Request Early Access
              </button>
            </div>
          </div>
        </div>
      </section> */}

      {/* Screenshot Carousel Section */}
      <section className="screenshot-carousel">
        <div className="container">
          <div className="section-header">
            <h2>See Question Answer Pro in Action</h2>
          </div>
          
          <div className="carousel-container">
            <div className="carousel-wrapper">
              <button 
                className="carousel-nav carousel-prev" 
                onClick={prevSlide}
                aria-label="Previous screenshot"
              >
                <ChevronLeft size={24} />
              </button>
              
              <div className="carousel-slides">
                <div 
                  className="carousel-track" 
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {screenshots.map((screenshot, index) => (
                    <div key={index} className="carousel-slide">
                      <div className="slide-content">
                        <img 
                          src={process.env.PUBLIC_URL + screenshot.src}
                          alt={screenshot.title}
                          className="slide-image"
                        />
                        <div className="slide-text">
                          <h3>{screenshot.title}</h3>
                          <p>{screenshot.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <button 
                className="carousel-nav carousel-next" 
                onClick={nextSlide}
                aria-label="Next screenshot"
              >
                <ChevronRight size={24} />
              </button>
            </div>
            
            <div className="carousel-indicators">
              {screenshots.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Build Your Digital Twin?</h2>
            <div className="cta-buttons">
              <button className="cta-primary large" onClick={handleRequestEarlyAccess}>
                {isAuthenticated 
                  ? 'Go to Build'
                  : 'Request Early Access'
                }
                <ArrowRight size={24} />
              </button>
              <button className="cta-secondary large" onClick={handleViewMarketplace}>
                Browse Marketplace
              </button>
            </div>
            <div className="cta-features">
              <div className="cta-feature">
                <CheckCircle size={20} />
                <span>No credit card required</span>
              </div>
              <div className="cta-feature">
                <CheckCircle size={20} />
                <span>Free forever plan available</span>
              </div>
              <div className="cta-feature">
                <CheckCircle size={20} />
                <span>Setup in under 5 minutes</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img 
                src={process.env.PUBLIC_URL + "/YourQuestionsAnsweredLogo.png"} 
                alt="Question Answer Pro Logo" 
                width={32} 
                height={32}
                style={{ objectFit: 'contain' }}
              />
              <span>Question Answer Pro</span>
            </div>
            <div className="footer-links">
              <a href="#features">Features</a>
              <Link to="/privacy">Privacy Policy</Link>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Question Answer Pro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
